import { formatDateTime } from '@/features/common/formatDate';
import type { LoginRefusal } from '@/features/auth/types/LoginRefusal';
import type { PasswordChangeErrors } from '@/features/auth/types/PasswordChangeErrors';
import type { PasswordChangeValues } from '@/features/auth/types/PasswordChangeValues';
import type { AdminAuthKey } from '@/i18n/dictionaries/adminAuth';
import type { Language } from '@/i18n/types';

/** The server's own floor (AdminPasswordPolicy.MIN_LENGTH), counted in characters. */
export const ADMIN_PASSWORD_MIN_LENGTH = 12;
/** The server's ceiling (AdminPasswordPolicy.MAX_BYTES): bcrypt reads no more than 72 bytes. */
export const ADMIN_PASSWORD_MAX_BYTES = 72;

/**
 * What is wrong with a new administration password, by the server's rule: at least twelve
 * characters (a letter with diacritics counts once), at most 72 bytes of UTF-8 (where it counts
 * twice), and not the username. Null when it holds.
 */
export function passwordPolicyError(password: string, username?: string): AdminAuthKey | null {
  if ([...password].length < ADMIN_PASSWORD_MIN_LENGTH) return 'passwordTooShort';
  if (new TextEncoder().encode(password).length > ADMIN_PASSWORD_MAX_BYTES) {
    return 'passwordTooLong';
  }
  if (username && password === username) return 'passwordEqualsUsername';
  return null;
}

/** The password change form's own checks, before the server's. */
export function passwordChangeErrors(values: PasswordChangeValues): PasswordChangeErrors {
  const errors: PasswordChangeErrors = {};
  if (!values.currentPassword) errors.currentPassword = 'currentRequired';
  const policy = passwordPolicyError(values.newPassword);
  if (policy) errors.newPassword = policy;
  else if (values.newPassword === values.currentPassword) errors.newPassword = 'passwordUnchanged';
  if (values.confirmation !== values.newPassword) errors.confirmation = 'confirmationMismatch';
  return errors;
}

/** When a locked credential opens again, in the page's words; null for a value that does not parse. */
export function lockedUntilLabel(
  lockedUntil: string | null | undefined,
  language: Language,
): string | null {
  if (!lockedUntil) return null;
  const label = formatDateTime(lockedUntil, language);
  return label || null;
}

/**
 * The one line the sign-in shows for a refusal: the server's own sentence where it sent one — a
 * locked credential's already names the time it opens again, so the time is not said twice — and,
 * for a 423 that came without one, when the credential opens again as this page words it.
 */
export function loginRefusal(detail: string | null, lockedUntil: string | null): LoginRefusal {
  if (detail) return { kind: 'server', text: detail };
  if (lockedUntil) return { kind: 'locked', when: lockedUntil };
  return { kind: 'generic' };
}
